import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import emailjs from '@emailjs/browser';
import { useNavigate } from 'react-router-dom';
import { services, budgetOptions } from '../data';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';

export const QuotationModal = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    projectType: '',
    budget: '',
    location: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // EmailJS configuration
      await emailjs.send(
        process.env.REACT_APP_EMAILJS_SERVICE_ID,
        process.env.REACT_APP_EMAILJS_QUOTATION_TEMPLATE_ID,
        {
          name: formData.fullName,
          from_email: formData.email,
          phone: formData.phone,
          project_type: formData.projectType,
          title: formData.projectType,
          budget: formData.budget,
          location: formData.location,
          message: formData.message,
          time: new Date().toLocaleString(),
          to_email: 'contact@aashigroups.in'
        },
        process.env.REACT_APP_EMAILJS_PUBLIC_KEY
      );

      toast.success('Thank you! We will contact you within 24 hours.');
      if (Array.isArray(window.dataLayer)) {
        window.dataLayer.push({ event: 'conversion', conversion_type: 'quotation' });
      }
      setFormData({
        fullName: '',
        email: '',
        phone: '',
        projectType: '',
        budget: '',
        location: '',
        message: ''
      });
      onClose();
      navigate('/thank-you', { state: { type: 'quotation' }, replace: true });
    } catch (error) {
      console.error('EmailJS Error:', error);
      toast.error('Failed to send. Please try again or call us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-black/60 backdrop-blur-sm animate-in fade-in duration-300">
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="bg-white rounded-xl md:rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col animate-in zoom-in-95 duration-300">
          <div className="flex-shrink-0 bg-gradient-to-r from-brand-600 to-brand-700 px-4 sm:px-6 md:px-8 py-4 md:py-6 flex justify-between items-start md:items-center rounded-t-xl md:rounded-t-2xl">
            <div className="flex-1 pr-4">
              <h2 className="text-xl sm:text-2xl font-heading font-bold text-white">Request Private Quotation</h2>
              <p className="text-brand-100 text-xs sm:text-sm mt-1">Let's bring your dream home to life</p>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:text-brand-100 transition-colors duration-200 flex-shrink-0"
            >
              <X className="w-5 h-5 sm:w-6 sm:h-6" />
            </button>
          </div>

        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
          <div className="overflow-y-auto flex-1 px-4 sm:px-6 md:px-8 pt-4 sm:pt-6 md:pt-8 space-y-4 sm:space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Full Name *
              </label>
              <Input
                name="fullName"
                value={formData.fullName}
                onChange={handleChange}
                placeholder="Aashi Constructions"
                required
                className="border-gray-300 focus:border-brand-600 focus:ring-brand-600 text-base"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email Address *
              </label>
              <Input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="aashi@example.com"
                required
                className="border-gray-300 focus:border-brand-600 focus:ring-brand-600 text-base"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Phone Number *
              </label>
              <Input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="+91 98765 43210"
                required
                className="border-gray-300 focus:border-brand-600 focus:ring-brand-600 text-base"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Project Type *
              </label>
              <Select
                name="projectType"
                value={formData.projectType}
                onValueChange={(val) => setFormData({ ...formData, projectType: val })}
              >
                <SelectTrigger className="w-full h-10 text-base bg-white border-gray-300 focus:ring-brand-600 focus:border-brand-600 md:text-sm">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  {services.map((s) => (
                    <SelectItem key={s.id} value={s.title.toLowerCase().replace(/\s+/g, '-')}>
                      {s.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Budget Range *
              </label>
              <Select
                name="budget"
                value={formData.budget}
                onValueChange={(val) => setFormData({ ...formData, budget: val })}
              >
                <SelectTrigger className="w-full h-10 text-base bg-white border-gray-300 focus:ring-brand-600 focus:border-brand-600 md:text-sm">
                  <SelectValue placeholder="Select budget" />
                </SelectTrigger>
                <SelectContent>
                  {budgetOptions.map((opt, i) => (
                    <SelectItem key={i} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Project Location *
              </label>
              <Input
                name="location"
                value={formData.location}
                onChange={handleChange}
                placeholder="Mumbai, Maharashtra"
                required
                className="border-gray-300 focus:border-brand-600 focus:ring-brand-600 text-base"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Project Details
            </label>
            <Textarea
              name="message"
              value={formData.message}
              onChange={handleChange}
              placeholder="Tell us about your vision, requirements, timeline, or any specific requests..."
              rows={4}
              className="border-gray-300 focus:border-brand-600 focus:ring-brand-600 resize-none text-base"
            />
          </div>
          </div>

          <div className="flex-shrink-0 px-4 sm:px-6 md:px-8 pb-4 sm:pb-6 md:pb-8 space-y-4">
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 pt-4">
              <Button
                type="button"
                onClick={onClose}
                variant="outline"
                className="hidden sm:flex flex-1 border-gray-300 hover:bg-gray-50 h-11 text-base"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting}
                className="flex-1 bg-brand-600 hover:bg-brand-700 text-white h-11 text-base"
              >
                {isSubmitting ? 'Submitting...' : 'Submit Request'}
              </Button>
            </div>

            <p className="text-xs text-gray-500 text-center">
              By submitting this form, you agree to our privacy policy and terms of service.
              We will contact you within 24 hours.
            </p>
          </div>
        </form>
        </div>
      </div>
    </div>
  );
};
